import { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import Avatar from '../ui/avatar-initials';
import { firstName } from '../../lib/play';
import ScoreEntry from './ScoreEntry';

/** Tap 4 players: first two → Team A, next two → Team B. Then enter the score. */
export default function QuickMatchForm({ data, sessionId, players, ratingOf, onLogged }) {
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const nm = (id) => firstName(ratingOf[id]?.name || 'Guest');

  const tap = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < 4 ? [...p, id] : p));
  const teamA = picked.slice(0, 2), teamB = picked.slice(2, 4);

  const submit = async (scoreA, scoreB) => {
    setBusy(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/matches`, { team_a: teamA, team_b: teamB, score_a: scoreA, score_b: scoreB });
      toast.success('Match logged');
      setPicked([]);
      onLogged(r.data.events);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not log match');
    } finally {
      setBusy(false);
    }
  };

  if (!data.can_score) return <p className="text-xs text-amber-700 dark:text-amber-300 text-center py-6">Only scorers can log matches.</p>;

  const all = players.concat(data.candidates.map((c) => c.id), data.members.map((m) => m.id));
  const uniq = [...new Set(all)];

  return (
    <div className="space-y-3" data-testid="quick-match-form">
      <p className="text-xs text-muted-foreground">Tap 4 players — first two are Team A, next two Team B.</p>
      <div className="grid grid-cols-2 gap-2 text-center">
        <Slot label="Team A" ids={teamA} nm={nm} testId="quick-team-a" />
        <Slot label="Team B" ids={teamB} nm={nm} testId="quick-team-b" />
      </div>
      <div className="flex flex-wrap gap-2" data-testid="quick-player-chips">
        {uniq.map((id) => {
          const idx = picked.indexOf(id);
          const p = ratingOf[id];
          return (
            <button key={id} type="button" onClick={() => tap(id)} data-testid={`quick-chip-${id}`}
              className={`inline-flex items-center gap-1.5 pl-1 pr-3 h-9 rounded-full border text-xs font-semibold transition-colors ${idx >= 0 ? (idx < 2 ? 'bg-sky-500/15 border-sky-500/60' : 'bg-rose-500/15 border-rose-500/60') : 'bg-background border-border text-muted-foreground'}`}>
              <Avatar name={p?.name || 'G'} size="xs" /> {nm(id)}
              {idx >= 0 && <span className="text-xs font-black">{idx < 2 ? 'A' : 'B'}</span>}
            </button>
          );
        })}
      </div>
      {picked.length === 4 && (
        <ScoreEntry pointsToWin={data.settings.points_to_win} busy={busy} onSubmit={submit} onCancel={() => setPicked([])}
          nameA={teamA.map(nm).join(' & ')} nameB={teamB.map(nm).join(' & ')} />
      )}
    </div>
  );
}

function Slot({ label, ids, nm, testId }) {
  return (
    <div className="rounded-xl border p-2 min-h-[56px]" data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-bold truncate">{ids.length ? ids.map(nm).join(' & ') : '—'}</p>
    </div>
  );
}
